import { isInitialized, trackEvent } from "@playlive/adobe-analytics";

/**
 * Fire-and-forget wrapper around `trackEvent` from `@playlive/adobe-analytics`.
 *
 * Two reasons this exists rather than calling `trackEvent` directly:
 *
 * 1. **It cannot throw.** The underlying `trackEvent` calls `getConfig()`, which
 *    throws when `initAdobeAnalytics` has not run. Most call sites here sit on
 *    auth and chatbot code paths, where an analytics failure must never take the
 *    user's flow down with it.
 * 2. **It works outside React.** `useAnalyticsEvent` is the right tool inside
 *    components; the OAuth popup handler in `@/util/tiltifyHelper` is a plain
 *    module and has no hook to hang off.
 *
 * Note on scope: this app reports most *clicks* passively, via `data-track`
 * attributes that Adobe Launch rules bind to. Events pushed through here are
 * deliberately limited to things a DOM click rule cannot observe — the
 * **outcome** of an async flow, and the context attached to it — so the two
 * mechanisms stay complementary rather than double-counting the same click.
 */
export function trackAppEvent(event: {
  eventName: string;
  eventAction: "success" | "failure";
  [key: string]: unknown;
}): void {
  if (!isInitialized()) {
    return;
  }

  try {
    trackEvent(event);
  } catch {
    // Analytics is strictly best-effort; never surface a reporting fault to the
    // user or interrupt the calling flow.
  }
}

/**
 * Normalise an unknown thrown value into a short, PII-free string safe to put
 * on an analytics event. Keeps the message only — stacks can carry file paths
 * and query strings we do not want leaving the browser.
 */
export function describeError(error: unknown): string {
  if (error instanceof Error) {
    return error.message.slice(0, 200);
  }
  if (typeof error === "string") {
    return error.slice(0, 200);
  }
  if (error && typeof error === "object" && "statusText" in error) {
    return String((error as { statusText: unknown }).statusText).slice(0, 200);
  }
  return "unknown error";
}
